import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

const SpinWheel = ({ currentStudents = [] }) => {
  const [names, setNames] = useState([]);
  const [inputName, setInputName] = useState('');
  const [winner, setWinner] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const spinDuration = 5000;

  const [spinSound, winnerSound] = useMemo(() => [
    new Audio('https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3'),
    new Audio('https://assets.mixkit.co/active_storage/sfx/2017/2017-preview.mp3')
  ], []);

  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  // Modal açıldığında sınıf listesini otomatik yükleme
  useEffect(() => {
    if (currentStudents && currentStudents.length > 0) {
      const firstNames = currentStudents.map(s => s?.name?.trim().split(' ')[0] || 'Öğrenci');
      setNames(firstNames);
    }
  }, [currentStudents]);

  const drawWheel = useCallback((angle = 0) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const radius = canvas.width / 2;
    const segmentAngle = names.length > 0 ? (2 * Math.PI) / names.length : 2 * Math.PI;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // İsim yoksa boş çark çizimi
    if (names.length === 0) {
      ctx.beginPath();
      ctx.arc(radius, radius, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#f1f5f9';
      ctx.fill();
      ctx.fillStyle = '#94a3b8';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Önce İsim Yükle', radius, radius);
      return;
    }

    // Dilimleri çiz
    names.forEach((name, index) => {
      const startAngle = index * segmentAngle + angle;
      const endAngle = startAngle + segmentAngle;
      ctx.beginPath();
      ctx.moveTo(radius, radius);
      ctx.arc(radius, radius, radius, startAngle, endAngle);
      ctx.fillStyle = `hsl(${(index * 360) / names.length}, 75%, 55%)`; // Renkler biraz daha canlı
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3; // Çizgiler daha belirgin
      ctx.stroke();

      ctx.save();
      ctx.translate(radius, radius);
      ctx.rotate(startAngle + segmentAngle / 2);
      ctx.fillStyle = 'white';
      // Yazı fontu tuval büyüdüğü için büyütüldü
      ctx.font = 'bold 22px Arial'; 
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, radius - 80, 0);
      ctx.restore();
    });

    // Çarkın tam ortasındaki beyaz nokta
    ctx.beginPath();
    ctx.arc(radius, radius, 30, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Seçici Ok (Üstte)
    ctx.beginPath();
    ctx.moveTo(radius, 30);
    ctx.lineTo(radius - 20, 70);
    ctx.lineTo(radius + 20, 70);
    ctx.closePath();
    ctx.fillStyle = '#ff4d4d';
    ctx.fill();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 3;
    ctx.stroke();
  }, [names]);

  const spinWheel = useCallback(() => {
    if (names.length === 0 || isSpinning) return;
    setIsSpinning(true);
    if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);

    spinSound.currentTime = 0;
    spinSound.play().catch(() => {});

    const randomAngle = Math.random() * 360 + 1080;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / spinDuration, 1);
      const easedProgress = easeOutQuart(progress);
      const angle = randomAngle * easedProgress;
      drawWheel((angle * Math.PI) / 180);

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
        spinSound.pause();
        spinSound.currentTime = 0;

        const finalAngleDegrees = (angle % 360 + 360) % 360;
        const segmentAngleDegrees = 360 / names.length;
        const adjustedAngle = (finalAngleDegrees + 90) % 360;
        const winnerIndex = Math.floor((360 - adjustedAngle) / segmentAngleDegrees) % names.length;

        setWinner(names[winnerIndex]);
        winnerSound.currentTime = 0;
        winnerSound.play().catch(() => {});
      }
    };
    animationFrameId.current = requestAnimationFrame(animate);
  }, [names, isSpinning, drawWheel, spinSound, winnerSound]);

  const setNumberWheel = useCallback((max) => {
    const numberList = Array.from({ length: max }, (_, i) => (i + 1).toString());
    setNames(numberList);
  }, []);

  const handleAddName = useCallback((e) => {
    if ((e.key === 'Enter' || e.type === 'click') && inputName.trim()) {
      setNames(prev => [...prev, inputName.trim()]);
      setInputName('');
    }
  }, [inputName]);

  const loadClass = useCallback(() => {
    if (!currentStudents || currentStudents.length === 0) {
        alert("Bu sınıfta öğrenci bulunamadı! (Lütfen sınıf seçili olduğundan emin olun)");
        return;
    }
    const firstNames = currentStudents.map(s => s?.name?.trim().split(' ')[0] || 'Öğrenci');
    setNames(firstNames);
  }, [currentStudents]);

  const removeLastName = useCallback(() => setNames(prev => prev.slice(0, -1)), []);
  const clearAllNames = useCallback(() => setNames([]), []);
  const handleRemoveName = useCallback((index) => setNames(prev => prev.filter((_, i) => i !== index)), []);
  
  const closeWinner = useCallback(() => { setWinner(null); winnerSound.pause(); }, [winnerSound]);
  const removeWinner = useCallback(() => { setNames(prev => prev.filter(name => name !== winner)); setWinner(null); winnerSound.pause(); }, [winner, winnerSound]);

  useEffect(() => { drawWheel(); return () => { if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current); }; }, [drawWheel]);

  return (
    // YENİ EKLENEN: flexWrap: 'wrap' sayesinde dar ekranlarda paneller alt alta kayar ve scrollbar çıkmaz.
    // boxSizing ayarları ile taşmalar engellendi.
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', alignItems: 'stretch', boxSizing: 'border-box', width: '100%' }}>
      
      {/* SOL PANEL: Seçenekler */}
      <div style={{ flex: '1 1 200px', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <h4 style={{ color: '#0d6efd', margin: '0 0 10px 0', fontSize: '1rem' }}>Sayı Çarkları</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {[4, 5, 6, 7, 8, 9, 10, 12].map(num => (
              <button key={num} onClick={() => setNumberWheel(num)} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', flexGrow: 1 }}>
                1-{num}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ color: '#0d6efd', margin: '0 0 10px 0', fontSize: '1rem' }}>İşlemler</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={loadClass} style={{ padding: '10px', background: '#198754', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              + Sınıfı Yükle
            </button>
            <button onClick={removeLastName} disabled={names.length === 0} style={{ padding: '8px', background: '#ffc107', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Son İsmi Sil
            </button>
            <button onClick={clearAllNames} disabled={names.length === 0} style={{ padding: '8px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Tümünü Temizle
            </button>
          </div>
        </div>
      </div>

      {/* ORTA PANEL: Çark */}
      {/* YENİ EKLENEN: Canvas'ın çözünürlüğü (width/height) yüksek tutuldu ama ekranda CSS ile (style width) esnek yapıldı. */}
      <div style={{ flex: '2 1 300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '260px' }}>
        <canvas 
            ref={canvasRef} 
            width="500" 
            height="500" 
            style={{ width: '100%', maxWidth: '350px', height: 'auto', marginBottom: '15px' }}
        ></canvas>
        <button 
            onClick={spinWheel} 
            disabled={isSpinning || names.length === 0} 
            style={{ padding: '12px 30px', fontSize: '1.2rem', fontWeight: 'bold', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '100%', maxWidth: '250px' }}
        >
          {isSpinning ? 'Dönüyor...' : 'ÇARKIFELEK'}
        </button>
      </div>

      {/* SAĞ PANEL: Liste */}
      {/* YENİ EKLENEN: overflowX: 'hidden' ile listenin yatayda taşması kesin olarak engellendi. */}
      <div style={{ flex: '1 1 200px', minWidth: '200px', maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden', background: '#f8fafc', padding: '15px', borderRadius: '8px', boxSizing: 'border-box' }}>
        <h4 style={{ color: '#0d6efd', margin: '0 0 10px 0', fontSize: '1rem' }}>Liste ({names.length})</h4>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <input 
            type="text" 
            value={inputName} 
            onChange={(e) => setInputName(e.target.value)} 
            onKeyDown={handleAddName} 
            placeholder="Ekle..." 
            style={{ flex: 1, minWidth: '0', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }} 
          />
          <button onClick={handleAddName} disabled={!inputName.trim()} style={{ padding: '8px 12px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Ekle</button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {names.map((name, index) => (
            <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: '0.9rem', wordBreak: 'break-word' }}>
              <span style={{ paddingRight: '10px' }}>{name}</span>
              <button onClick={() => handleRemoveName(index)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', flexShrink: 0 }}>Sil</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Kazanan Modalı */}
      {winner && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '40px', borderRadius: '15px', textAlign: 'center', animation: 'scaleUp 0.3s ease', maxWidth: '90%', boxSizing: 'border-box' }}>
            <h2 style={{ color: '#0d6efd', fontSize: '2rem', margin: '0 0 10px 0' }}>KAZANAN!</h2>
            <p style={{ fontSize: '3rem', fontWeight: 'bold', color: '#333', margin: '0 0 20px 0', wordBreak: 'break-word' }}>{winner}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={closeWinner} style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1.1rem' }}>Kapat</button>
              <button onClick={removeWinner} style={{ padding: '10px 20px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1.1rem' }}>Listeden Çıkar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheel;